import { supabase } from "../lib/supabase";

/**
 * Save a customer packing slip record in the database
 */
export const createCustomerPackingSlip = async (
  bookingId: number,
  filePath: string
) => {
  try {
    console.log("Creating customer packing slip record with:", {
      booking_id: bookingId,
      booking_packing_slip_file: filePath
    });
    
    // Insert new packing slip record
    const { data, error } = await supabase
      .from("booking_packing_slip")
      .insert([
        {
          booking_id: bookingId,
          booking_packing_slip_file: filePath
        }
      ])
      .select();

    if (error) {
      console.error("Database error during insert:", error);
      throw error;
    }

    return {
      success: true,
      data,
      error: null
    };
  } catch (error) {
    console.error("Error creating customer packing slip:", error);
    return {
      success: false,
      data: null,
      error
    };
  }
};

/**
 * Get all customer packing slips for a booking
 */
export const getCustomerPackingSlips = async (bookingId: number) => {
  try {
    console.log(`Fetching customer packing slips for booking ID: ${bookingId}`);
    const { data, error } = await supabase
      .from("booking_packing_slip")
      .select("*")
      .eq("booking_id", bookingId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Database error during select:", error);
      throw error;
    }

    console.log(`Found ${data?.length || 0} customer packing slips`);
    return {
      success: true,
      data,
      error: null
    };
  } catch (error) {
    console.error("Error fetching customer packing slips:", error);
    return {
      success: false,
      data: [],
      error
    };
  }
};

/**
 * Upsert (update or insert) a customer packing slip record
 * This prevents duplicates by updating existing records or creating new ones
 */
export const upsertCustomerPackingSlip = async (
  bookingId: number,
  filePath: string
) => {
  try {
    console.log("Upserting customer packing slip record with:", {
      booking_id: bookingId,
      booking_packing_slip_file: filePath
    });
    
    // First, get existing records
    const existingResult = await getCustomerPackingSlips(bookingId);
    
    if (existingResult.success && existingResult.data && existingResult.data.length > 0) {
      // Update the most recent record
      const latestRecord = existingResult.data[0]; // Already ordered by created_at desc
      console.log(`Updating existing record ID: ${latestRecord.id}`);
      
      const { data, error } = await supabase
        .from("booking_packing_slip")
        .update({
          booking_packing_slip_file: filePath,
          created_at: new Date().toISOString() // Update timestamp
        })
        .eq("id", latestRecord.id)
        .select();

      if (error) {
        console.error("Database error during update:", error);
        throw error;
      }

      // Clean up any duplicate records (keep only the updated one)
      if (existingResult.data.length > 1) {
        console.log(`Cleaning up ${existingResult.data.length - 1} duplicate records`);
        const duplicateIds = existingResult.data.slice(1).map(record => record.id);
        
        const { error: deleteError } = await supabase
          .from("booking_packing_slip")
          .delete()
          .in("id", duplicateIds);
          
        if (deleteError) {
          console.warn("Error cleaning up duplicates:", deleteError);
          // Don't fail the operation for cleanup errors
        }
      }

      return {
        success: true,
        data,
        error: null,
        action: 'updated'
      };
    } else {
      // No existing record, create new one
      console.log("No existing record found, creating new one");
      const result = await createCustomerPackingSlip(bookingId, filePath);
      return {
        ...result,
        action: 'created'
      };
    }
  } catch (error) {
    console.error("Error upserting customer packing slip:", error);
    return {
      success: false,
      data: null,
      error,
      action: 'failed'
    };
  }
};

/**
 * Clean up duplicate packing slip records for a booking (keeps only the most recent)
 */
export const cleanupDuplicatePackingSlips = async (bookingId: number) => {
  try {
    console.log(`Cleaning up duplicate packing slips for booking ID: ${bookingId}`);
    
    const existingResult = await getCustomerPackingSlips(bookingId);
    
    if (existingResult.success && existingResult.data && existingResult.data.length > 1) {
      // Keep only the most recent record (first in the array due to DESC order)
      const duplicateIds = existingResult.data.slice(1).map(record => record.id);
      
      console.log(`Found ${duplicateIds.length} duplicate records to remove`);
      
      const { error } = await supabase
        .from("booking_packing_slip")
        .delete()
        .in("id", duplicateIds);
        
      if (error) {
        throw error;
      }
      
      return {
        success: true,
        removedCount: duplicateIds.length,
        error: null
      };
    }
    
    return {
      success: true,
      removedCount: 0,
      error: null
    };
  } catch (error) {
    console.error("Error cleaning up duplicate packing slips:", error);
    return {
      success: false,
      removedCount: 0,
      error
    };
  }
};

/**
 * Delete a customer packing slip record and its associated file
 */
export const deleteCustomerPackingSlip = async (id: number, filePath: string) => {
  try {
    console.log(`Deleting customer packing slip with ID: ${id} and file: ${filePath}`);

    // Delete the file from storage
    const { error: storageError } = await supabase.storage
      .from("customerpackingslips")
      .remove([filePath]);

    if (storageError) {
      console.warn("Error deleting file from storage:", storageError);
      // Continue with database deletion even if file deletion fails
    }

    // Delete the database record
    const { error } = await supabase
      .from("booking_packing_slip")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Database error during delete:", error);
      throw error;
    }

    return {
      success: true,
      error: null
    };
  } catch (error) {
    console.error("Error deleting customer packing slip:", error);
    return {
      success: false,
      error
    };
  }
}; 